import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable, OnDestroy } from "@angular/core";
import {
  AngularFirestore,
  AngularFirestoreCollection,
  AngularFirestoreDocument,
  CollectionReference,
  Query,
} from "@angular/fire/firestore";
import { liveQuery } from "dexie";

import * as firebase from "firebase/app";
import "firebase/firestore";
import * as _ from "lodash";
import * as moment from "moment";
import { Subject } from "rxjs";
import { map, takeUntil } from "rxjs/operators";

import { environment } from "../../environments/environment";
import { User } from "../@core/data/users";
import { indexedDb } from "../@core/services/indexedDB.service";
import { UserService } from "../@core/services/user.service";
import { convertTimestamp } from "../@core/utils/convertTimestamps.service";
import { convertViToEn } from "../@core/utils/convertViToEn.service";
import { PaginationProduct, ProductSpecParams } from "../models/pagination";
import { Product } from "../models/product.model";
import { AngularFireFunctions } from "@angular/fire/functions";

@Injectable({
  providedIn: "root",
})
export class ProductService implements OnDestroy {
  visibility = environment.visibility;
  baseUrl = environment.apiUrl + "/Products";
  syncLive: boolean = environment.syncLive;
  userLogin: User;
  shopId: string = localStorage.getItem("shopId")
    ? localStorage.getItem("shopId")
    : "";
  //shopId: string = "";
  private dbPath = "/shops";
  products$ = liveQuery(() => indexedDb.products.toArray());
  subscribeProduct: any;
  products: Product[] = [];

  destroy$: Subject<void> = new Subject<void>();
  collRef: AngularFirestoreCollection<Product>;

  constructor(
    private db: AngularFirestore,
    private fns: AngularFireFunctions,
    private http: HttpClient,
    private userStore: UserService,
  ) {
    console.log("ProductService/constructor/shopId:", this.shopId);

    this.userStore
      .getCurrentUser()
      .pipe(takeUntil(this.destroy$))
      .subscribe((user: User) => {
        if (user) {
          this.userLogin = user;
          this.shopId = user.shopId;
          this.visibility = user.companyId
            ? user.companyId
            : environment.visibility;

          if (this.shopId !== user.shopId) {
            this.shopId = user.shopId;
          }
        }
      });

    indexedDb.products
      .toArray()
      .then((data) => {
        console.log("ProductService/indexedDb:", data);
        if (data.length > 0 && this.shopId !== "") {
          this.products = _.filter(data, { shopId: this.shopId });
        } else {
          this.products = data ? data : [];
        }
      })
      .catch((error) => {
        console.log("ProductService/indexedDb/error:", error);
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    // this.subscribeProduct.unsubscribe();
    console.log("ProductService/ngOnDestroy");
  }

  getProducts(shopId: string, fromDate?: Date, limit?: number): any {
    var db = firebase.default.firestore();
    var ref = db
      .collection(this.dbPath + `/${shopId}/products`)
      .where("isDeleted", "==", false)
      .where("isActive", "==", true);

    if (fromDate) {
      ref = ref.where("updatedOn", ">=", fromDate);
    }

    ref = ref.orderBy("updatedOn", "desc");

    if (limit > 0) {
      ref = ref.limit(limit);
    }

    return ref.get();
  }

  initIndexedDbProducts(shopId: string, isInit?: boolean) {
    let sub: any;
    const lastUpdated = localStorage.getItem(
      `${shopId.toLowerCase()}_products_updatedOn`,
    );
    isInit = !lastUpdated || isInit ? true : false;

    if (isInit) {
      sub = this.getProducts(shopId);
    } else {
      const fromDate =
        lastUpdated && moment(lastUpdated)
          ? moment(lastUpdated, "YYYY-MM-DD HH:mm").add(-1, "days").toDate()
          : moment().add(-5, "days").startOf("day").toDate();

      sub = this.getProducts(shopId, fromDate);
    }

    sub.then((querySnapshot) => {
      if (querySnapshot.docs.length > 0) {
        querySnapshot.forEach((doc) => {
          let prod = new Product();
          prod = doc.data();
          prod.id = doc.id;
          prod.createdOn = convertTimestamp(doc.data().createdOn);
          prod.updatedOn = convertTimestamp(doc.data().updatedOn);

          this.updateIndexedDb(prod);
        });
      }

      return this.getProductsChange(shopId);
    });
  }

  private getProductsChange(shopId: string) {
    this.db
      .collection<Product>(this.dbPath + `/${shopId}/products`, (ref) =>
        ref
          .where("shopId", "==", shopId)
          .orderBy("updatedOn", "desc")
          .limit(50),
      )
      .snapshotChanges()
      .pipe(
        map((changes) =>
          changes.map((c) => ({
            id: c.payload.doc.id,
            ...c.payload.doc.data(),
            createdOn: convertTimestamp(c.payload.doc.data().createdOn),
            updatedOn: convertTimestamp(c.payload.doc.data().updatedOn),
          })),
        ),
        takeUntil(this.destroy$),
      )
      .subscribe((dataChange) => {
        if (dataChange && dataChange.length > 0) {
          _.forEach(dataChange, (doc) => {
            let prod = new Product();
            prod = doc;
            prod.id = doc.id;
            prod.createdOn = convertTimestamp(prod.createdOn);
            prod.updatedOn = convertTimestamp(prod.updatedOn);

            if (prod.isDeleted === true) {
              this.deleteIndexedDb(prod.id);
            } else {
              this.updateIndexedDb(prod);
              if (prod.id === "NC7IPUHAX0Nk1uCwMb62") {
                console.log(
                  "🚀 ~ ProductService ~ getProductsChange ~ prod /NC7IPUHAX0Nk1uCwMb62:",
                  prod,
                );
              }
            }
          });

          localStorage.setItem(
            `${shopId.toLowerCase()}_products_updatedOn`,
            moment().format("YYYY-MM-DD HH:mm"),
          );
        }
      });
  }

  getAllProducts(shopId: string): AngularFirestoreCollection<Product> {
    return this.db.collection(this.dbPath + `/${shopId}/products`, (ref) => {
      let query: CollectionReference | Query = ref;
      query = query.where("isDeleted", "==", false);
      query = query.where("isActive", "==", true);
      query = query.orderBy("updatedOn", "desc");

      return query;
    });
  }

  getProductsStock(shopId: string): AngularFirestoreCollection<Product> {
    return this.db.collection(this.dbPath + `/${shopId}/products`, (ref) =>
      ref
        .where("isDeleted", "==", false)
        .where("isActive", "==", true)
        .where("isStock", "==", true)
        .orderBy("updatedOn", "desc"),
    );
  }

  getProductsStockByGroupCategory(
    shopId: string,
    groupId: string,
    categoryId: string,
  ): AngularFirestoreCollection<Product> {
    return this.db.collection(this.dbPath + `/${shopId}/products`, (ref) => {
      let query: CollectionReference | Query = ref;

      query = query.where("isDeleted", "==", false);
      query = query.where("isActive", "==", true);
      query = query.where("isStock", "==", true);

      if (groupId) {
        query = query.where("groupId", "==", groupId);
      }
      if (categoryId) {
        query = query.where("categoryId", "==", categoryId);
      }

      query = query.orderBy("updatedOn", "desc");
      return query;
    });
  }

  getProductsByGroupCategory(
    shopId: string,
    groupId: string,
    categoryId: string,
  ): AngularFirestoreCollection<Product> {
    return this.db.collection(this.dbPath + `/${shopId}/products`, (ref) => {
      let query: CollectionReference | Query = ref;

      query = query.where("isDeleted", "==", false);
      query = query.where("isActive", "==", true);

      if (groupId) {
        query = query.where("groupId", "==", groupId);
      }
      if (categoryId) {
        query = query.where("categoryId", "==", categoryId);
      }

      query = query.orderBy("updatedOn", "desc");
      return query;
    });
  }

  getProductsSync(
    shopId: string,
    timestamp?: number,
  ): AngularFirestoreCollection<Product> {
    timestamp = timestamp ? timestamp : 0;

    return this.db.collection(this.dbPath + `/${shopId}/products`, (ref) =>
      ref
        .where("shopId", "==", shopId)
        .where("timestamp", ">=", timestamp)
        .orderBy("timestamp", "desc")
        .limit(100),
    );
  }

  getProductsByUser(
    shopId: string,
    uid: string,
  ): AngularFirestoreCollection<Product> {
    return this.db.collection(this.dbPath + `/${shopId}/products`, (ref) =>
      ref
        .where("isDeleted", "==", false)
        .where("isActive", "==", true)
        .where("createdBy", "==", uid)
        .orderBy("updatedOn", "desc"),
    );
  }

  getProductsScrolled(
    shopId: string,
    uid: string,
    limit?: number,
    cursor?: any,
  ): any {
    if (!shopId || shopId === "") {
      shopId = uid;
    }

    var db = firebase.default.firestore();
    var ref = db
      .collection(this.dbPath + `/${shopId}/products`)
      .where("isDeleted", "==", false)
      .where("isActive", "==", true)
      .where("createdBy", "==", uid)
      .orderBy("updatedOn", "desc");

    ref = limit ? ref.limit(limit) : ref.limit(10);

    if (cursor) {
      ref = ref.startAfter(cursor);
    }

    return ref.get();
  }

  scanBarcodeOnline(shopId: string, barcode?: string): any {
    var db = firebase.default.firestore();
    var ref = db
      .collection(this.dbPath + `/${shopId}/products`)
      .where("isDeleted", "==", false)
      .where("isActive", "==", true)
      .where("barcode", "==", barcode)
      .orderBy("updatedOn", "desc");

    return ref.get();
  }

  get(shopId: string, id: string) {
    return this.db
      .collection(this.dbPath + `/${shopId}/products`)
      .doc(id)
      .valueChanges();
  }

  getById(shopId: string, id: string) {
    var db = firebase.default.firestore();
    var ref = db.collection(this.dbPath + `/${shopId}/products`).doc(id);

    return ref.get();
  }

  getProductDbIndexById(id: string): Product {
    const product = _.find(this.products, {
      id: id,
    });
    console.log("getProductDbIndexById", product);
    return product;
  }

  create(product: Product): any {
    console.log("create product before:", product);

    if (!product.barcode || product.barcode === "") {
      product.barcode = this.generateBarcodeEAN13();
    } else {
      product.barcode = product.barcode.trim();
    }

    product.title = product.title
      ? product.title.charAt(0).toUpperCase() + product.title.slice(1)
      : "";
    product.titleEn = convertViToEn(product.title);
    product.titleEns = product.titleEn ? product.titleEn.split(" ") : [];

    product.unitId = product.unitId ? product.unitId : "";
    product.unitName = product.unitName ? product.unitName : "Cái";

    product.price = product.price ? product.price : 0;
    product.wsPrice = product.wsPrice ? product.wsPrice : 0;
    product.costPrice = product.costPrice ? product.costPrice : 0;

    product.groupId = product.groupId ? product.groupId : "";
    product.groupName = product.groupName ? product.groupName : "";
    product.categoryId = product.categoryId ? product.categoryId : "";
    product.categoryName = product.categoryName ? product.categoryName : "";
    product.supplierId = product.supplierId ? product.supplierId : "";
    product.supplierName = product.supplierName ? product.supplierName : "";
    product.producerId = product.producerId ? product.producerId : "";
    product.producerName = product.producerName ? product.producerName : "";
    product.originId = product.originId ? product.originId : "";
    product.originName = product.originName ? product.originName : "";
    product.sizeId = product.sizeId ? product.sizeId : "";
    product.sizeName = product.sizeName ? product.sizeName : "";
    product.colorId = product.colorId ? product.colorId : "";
    product.colorName = product.colorName ? product.colorName : "";
    product.stockMin = product.stockMin ? product.stockMin : 0;
    product.stockMax = product.stockMax ? product.stockMax : 0;
    product.isStock =
      product.isStock == null || typeof product.isStock === "undefined"
        ? true
        : product.isStock;

    product.initQuantity = product.initQuantity ? product.initQuantity : 0;
    product.initAmount = product.initAmount ? product.initAmount : 0;
    product.receiveQuantity = product.initQuantity;
    product.receiveAmount = product.initAmount;
    product.balanceQuantity = product.initQuantity;
    product.balanceAmount = product.initAmount;

    product.isArchived = true;
    product.timestamp = new Date().getTime();
    product.isSynced = true;
    product.syncedOn = new Date();
    product.isActive = true;
    product.isDeleted = false;
    product.createdOn = new Date();
    product.createdBy = this.userLogin.uid;
    product.updatedOn = new Date();
    product.updatedBy = this.userLogin.uid;
    product.author = this.userLogin;
    product.visibility = this.visibility;

    if (product.id) delete product.id;

    product.shopId = product.shopId ? product.shopId : this.userLogin.shopId;
    this.collRef = this.db.collection(
      this.dbPath + `/${product.shopId}/products`,
    );

    return this.collRef.add({ ...product }).then((docRef) => {
      product.id = docRef.id;
      console.log("create product after:", product);

      this.updateIndexedDb(product);
      return product;
    });
  }

  update(product: Product): any {
    console.log("update product:", product);

    const updatedOn = new Date();
    const updatedBy = this.userLogin.uid;

    product.shopId = product.shopId ? product.shopId : this.userLogin.shopId;
    product.titleEn = convertViToEn(product.title || "");
    product.titleEns = product.titleEn ? product.titleEn.split(" ") : [];

    this.collRef = this.db.collection(
      this.dbPath + `/${product.shopId}/products`,
    );
    return this.collRef.doc(product.id).update({
      title: product.title
        ? product.title.charAt(0).toUpperCase() + product.title.slice(1)
        : "",
      titleEn: product.titleEn,
      titleEns: product.titleEns,
      description: product.description ? product.description : "",
      price: product.price ? Number(product.price) : 0,
      wsPrice: product.wsPrice ? Number(product.wsPrice) : 0,
      costPrice: product.costPrice ? Number(product.costPrice) : 0,
      barcode: product.barcode ? product.barcode : "",
      qrcode: product.qrcode ? product.qrcode : "",
      code: product.code ? product.code : "",

      expDate: product.expDate ? product.expDate : new Date(),
      isStock:
        product.isStock == null || typeof product.isStock === "undefined"
          ? true
          : product.isStock,
      isService: product.isService ? product.isService : false,
      isFavorite: product.isFavorite ? product.isFavorite : false,
      isPopular: product.isPopular ? product.isPopular : false,
      isNew: product.isNew ? product.isNew : false,
      isSaleOff: product.isSaleOff ? product.isSaleOff : false,
      saleOffPrice: product.saleOffPrice ? product.saleOffPrice : 0,
      stockMin: product.stockMin ? product.stockMin : 0,
      stockMax: product.stockMax ? product.stockMax : 0,
      initQuantity: product.initQuantity ? Number(product.initQuantity) : 0,
      initAmount: product.initAmount ? Number(product.initAmount) : 0,

      coverUrl: product.coverUrl ? product.coverUrl : "",
      sourceType: product.sourceType ? product.sourceType : "",
      content: product.content ? product.content : "",
      videoUrl: product.videoUrl ? product.videoUrl : "",
      imagesUrl: product.imagesUrl ? product.imagesUrl : [],
      tags: product.tags ? product.tags : [],
      note1: product.note1 ? product.note1 : "",
      note2: product.note2 ? product.note2 : "",

      unitId: product.unitId ? product.unitId : "",
      unitName: product.unitName ? product.unitName : "",
      groupId: product.groupId ? product.groupId : "",
      groupName: product.groupName ? product.groupName : "",
      categoryId: product.categoryId ? product.categoryId : "",
      categoryName: product.categoryName ? product.categoryName : "",
      supplierId: product.supplierId ? product.supplierId : "",
      supplierName: product.supplierName ? product.supplierName : "",
      producerId: product.producerId ? product.producerId : "",
      producerName: product.producerName ? product.producerName : "",
      originId: product.originId ? product.originId : "",
      originName: product.originName ? product.originName : "",
      sizeId: product.sizeId ? product.sizeId : "",
      sizeName: product.sizeName ? product.sizeName : "",
      colorId: product.colorId ? product.colorId : "",
      colorName: product.colorName ? product.colorName : "",

      isArchived: product.isArchived ? product.isArchived : false,
      updatedOn: updatedOn,
      updatedBy: updatedBy,
      timestamp: new Date().getTime(),
    });
  }

  updatePrice(
    shopId: string,
    id: string,
    title: string,
    unitName: string,
    price: number,
    costPrice: number,
  ): Promise<void> {
    console.log("🚀 ~ ProductService ~ updatePrice ~ price:", price);
    console.log("🚀 ~ ProductService ~ updatePrice ~ title:", title);
    console.log("🚀 ~ ProductService ~ updatePrice ~ id:", id);
    console.log("🚀 ~ ProductService ~ updatePrice ~ shopId:", shopId);

    const updatedOn = new Date();
    const updatedBy = this.userLogin.uid;
    const titleEn = convertViToEn(title);
    const titleEns = titleEn ? titleEn.split(" ") : [];

    this.collRef = this.db.collection(this.dbPath + `/${shopId}/products`);
    return this.collRef.doc(id).update({
      title: title.charAt(0).toUpperCase() + title.slice(1),
      titleEn: titleEn,
      titleEns: titleEns,
      unitName: unitName,
      price: price ? price : 0,
      costPrice: costPrice ? costPrice : 0,
      updatedOn: updatedOn,
      updatedBy: updatedBy,
      timestamp: new Date().getTime(),
    });
  }

  updateInitInventory(
    shopId: string,
    productId: string,
    isInitInventory: boolean,
    initInventoryId: string,
    initInventoryQuantity: number,
    initInventoryDate: Date,
  ): Promise<void> {
    const updatedOn = new Date();
    const updatedBy = this.userLogin.uid;

    this.collRef = this.db.collection(this.dbPath + `/${shopId}/products`);
    return this.collRef.doc(productId).update({
      isInitInventory: isInitInventory,
      initInventoryId: initInventoryId,
      initInventoryQuantity: initInventoryQuantity,
      initInventoryDate: initInventoryDate,
      updatedOn: updatedOn,
      updatedBy: updatedBy,
      timestamp: new Date().getTime(),
    });
  }

  delete(shopId: string, id: string): Promise<void> {
    const updatedOn = new Date();
    const updatedBy = this.userLogin.uid;
    this.collRef = this.db.collection(this.dbPath + `/${shopId}/products`);

    return this.collRef.doc(id).update({
      isDeleted: true,
      updatedOn: updatedOn,
      updatedBy: updatedBy,
      timestamp: new Date().getTime(),
    });
  }

  generateBarcodeEAN13(): string {
    const pre = "893";
    const companyCode = "03166"; //5so
    const code = "1234"; //4so
    // let barcode: string = pre + companyCode + code;

    let barcode = "";
    const currentDate = new Date();
    const year = (currentDate.getFullYear() % 1000) % 100;
    barcode += year;

    const month = currentDate.getMonth() + 1;
    barcode += month < 10 ? "0" + month.toString() : month.toString();

    const day = currentDate.getDate();
    barcode += day < 10 ? "0" + day.toString() : day.toString();

    const hour = currentDate.getHours();
    barcode += hour < 10 ? "0" + hour.toString() : hour.toString();

    const min = currentDate.getMinutes();
    barcode += min < 10 ? "0" + min.toString() : min.toString();

    const sec = currentDate.getSeconds();
    barcode += sec < 10 ? "0" + sec.toString() : sec.toString();

    const checkSum = this.getLastEan13Digit(barcode);
    barcode = barcode + checkSum;
    return barcode;
  }

  private getLastEan13Digit(ean) {
    if (!ean || ean.length !== 12)
      throw new Error("Invalid EAN 13, should have 12 digits");
    const multiply = [1, 3];
    let total = 0;
    ean.split("").forEach((letter, index) => {
      total += parseInt(letter, 10) * multiply[index % 2];
    });
    const base10Superior = Math.ceil(total / 10) * 10;
    return base10Superior - total;
  }

  //offline db - syncLive

  sync(content: Product) {
    content.isDeleted
      ? this.deleteIndexedDb(content.id)
      : this.updateIndexedDb(content);

    //sync data to offline DB
    const syncData = _.clone(content);

    syncData.syncedOn ? delete syncData.syncedOn : null;
    syncData.content ? delete syncData.content : null;
    syncData.fsId = syncData.id ? syncData.id : "";
    // console.log("Products/syncData:", syncData);

    if (environment.syncLive) {
      this.http.post(this.baseUrl + "/Sync", syncData).subscribe({
        next: () => {},
        error: (err) => {
          console.log("Products/syncData/err:", err);
        },
      });
    }
  }

  getPaginationProduct(productSpec: ProductSpecParams) {
    let params = new HttpParams();
    if (productSpec.search)
      params = params.append("search", productSpec.search);

    if (productSpec.isActive != null)
      params = params.append("isActive", productSpec.isActive.toString());

    if (productSpec.pageIndex)
      params = params.append("pageIndex", productSpec.pageIndex.toString());

    if (productSpec.pageSize)
      params = params.append("pageSize", productSpec.pageSize.toString());

    if (productSpec.sort)
      params = params.append("sort", productSpec.sort.toString());

    return this.http
      .get<PaginationProduct>(this.baseUrl, {
        observe: "response",
        params,
      })
      .pipe(
        map((response) => {
          // this.data = response.body.data;
          // this.pagination = response.body;
          console.log("getPaginationProduct: ", response);
          return response.body;
        }),
      );
  }

  scanBarcodeLocal(barcode: string) {
    return this.http.get<Product>(this.baseUrl + "/Barcode/" + barcode);
  }

  createOffline(content: Product) {
    if (!content.barcode || content.barcode === "") {
      content.barcode = this.generateBarcodeEAN13();
    } else {
      content.barcode = content.barcode.trim();
    }

    content.title =
      content.title.charAt(0).toUpperCase() + content.title.slice(1);
    content.titleEn = convertViToEn(content.title);
    content.titleEns = content.titleEn ? content.titleEn.split(" ") : [];

    content.unitId = content.unitId ? content.unitId : "";
    content.unitName = content.unitName ? content.unitName : "Cái";
    content.shopId = content.shopId ? content.shopId : this.userLogin.shopId;

    return this.http.post(this.baseUrl, content).pipe(
      map((response: Product) => {
        console.log("Products/createOffline:", response);

        //sync data to online DB
        const prodRef: AngularFirestoreDocument<any> = this.db.doc(
          this.dbPath + `/${content.shopId}/products/${response.id}`,
        );
        prodRef.set(
          {
            barcode: content.barcode,
            qrcode: content.qrcode ? content.qrcode : "",
            code: content.code ? content.code : "",
            title: content.title,
            titleEn: content.titleEn,
            titleEns: content.titleEns,
            description: content.description ? content.description : "",
            price: content.price ? content.price : 0,
            wsPrice: content.wsPrice ? content.wsPrice : 0,
            costPrice: content.costPrice ? content.costPrice : 0,
            unitId: content.unitId ? content.unitId : "",
            unitName: content.unitName,
            shopId: content.shopId,
            createdOn: new Date(),
            createdBy: this.userLogin.uid,
            updatedOn: new Date(),
            updatedBy: this.userLogin.uid,
            timestamp: new Date().getTime(),
          },
          {
            merge: true,
          },
        );

        return response;
      }),
    );
  }

  updateOffline(content: Product) {
    content.title =
      content.title.charAt(0).toUpperCase() + content.title.slice(1);
    content.unitId = content.unitId ? content.unitId : "";
    content.unitName = content.unitName ? content.unitName : "Cái";
    content.shopId = content.shopId ? content.shopId : this.userLogin.shopId;

    return this.http.put(this.baseUrl, content).pipe(
      map((response: Product) => {
        console.log("Products/updateOffline:", response);

        //sync data to online DB
        let syncDataOnline = _.clone(content);
        syncDataOnline.id = content.fsId ? content.fsId : response.id;
        this.update(syncDataOnline);

        return response;
      }),
    );
  }

  deleteOffline(content: Product) {
    return this.http.delete<Product>(this.baseUrl + "/" + content.id).pipe(
      map((response) => {
        //sync data to online DB
        let syncDataOnline = _.clone(content);
        syncDataOnline.id = content.fsId ? content.fsId : response.id;
        this.delete(syncDataOnline.shopId, syncDataOnline.id);
        return response;
      }),
    );
  }

  //scan product
  private getProductInLocalApi(barcode): any {
    var promise = new Promise<any>((resolve, reject) => {
      //method 2: call server offline
      this.scanBarcodeLocal(barcode).subscribe(
        (prod) => {
          console.log("scanBarcodeLocal:", prod);
          if (prod) {
            prod.id = prod.fsId ? prod.fsId : prod.id;
            resolve(prod);
          } else {
            reject(new Error("Không tìm thấy sản phẩm"));
          }
        },
        (error) => {
          reject(new Error("Không tìm thấy sản phẩm"));
        },
      );
    });
    return promise;
  }

  private getProductInOnline(shopId, barcode): any {
    var promise = new Promise<any>((resolve, reject) => {
      //method 3: call server online
      this.scanBarcodeOnline(shopId, barcode)
        .then((querySnapshot) => {
          if (querySnapshot.docs.length > 0) {
            let prods: Product[] = [];
            querySnapshot.forEach((doc) => {
              let data = new Product();
              data = doc.data();
              data.id = doc.id;
              data.createdOn = convertTimestamp(doc.data().createdOn);
              prods.push(data);
            });
            console.log("scanBarcodeOnline:", prods);

            resolve(prods[0]);
          } else {
            reject(new Error("Không tìm thấy sản phẩm"));
          }
        })
        .catch((error) => {
          reject(new Error("Không tìm thấy sản phẩm"));
        });
    });
    return promise;
  }

  getProduct(shopId, barcode): any {
    var promise = new Promise<Product>((resolve, reject) => {
      if (!this.syncLive) {
        const prod = this.getProductInOnline(shopId, barcode);
        if (prod) {
          resolve(prod);
        } else {
          reject(new Error("Không tìm thấy sản phẩm"));
        }
      } else {
        this.getProductInLocalApi(barcode)
          .then((prod) => {
            resolve(prod);
          })
          .catch((error) => {
            const prod = this.getProductInOnline(shopId, barcode);
            if (prod) {
              //sync data to offline DB
              this.sync(prod);
              resolve(prod);
            } else {
              reject(new Error("Không tìm thấy sản phẩm"));
            }
          });
      }
    });

    return promise;
  }

  searchProducts(strSearch: string): any {
    strSearch = strSearch.toLowerCase();
    const strSearchEn = convertViToEn(strSearch);
    const strSearchEns = strSearchEn.split(" ");

    let resultFilters: Product[] = [];
    let resultMatchings: Product[] = [];
    var promise = new Promise<Product[]>((resolve, reject) => {
      _.forEach(this.products, (item, index) => {
        const title = item.title ? item.title.toLowerCase() : "";
        const description = item.description
          ? item.description.toLowerCase()
          : "";
        const barcode = item.barcode ? item.barcode.toLowerCase() : "";
        if (!item.titleEns) {
          item.titleEn = convertViToEn(item.title);
          item.titleEns = item.titleEn ? item.titleEn.split(" ") : [];
          this.products[index] = item;
        }

        if (
          (title && title.includes(strSearch)) ||
          (description && description.includes(strSearch)) ||
          (barcode && barcode.includes(strSearch))
        ) {
          item.matchFilter = true;
          resultFilters.push(item);
        } else {
          var matchings = _.intersection(item.titleEns, strSearchEns);
          if (matchings.length > 0) {
            item.matchFilter = false;
            item.matchIndex = matchings.length;
            item.matchings = matchings;
            resultMatchings.push(item);
          }
        }
      });

      resultMatchings = _.sortBy(resultMatchings, ["matchIndex"]);
      resultMatchings = _.reverse(resultMatchings);
      resultMatchings = _.take(resultMatchings, 20);

      let result: Product[] = [...resultFilters, ...resultMatchings];

      if (result.length > 0) {
        resolve(result);
      } else {
        reject(new Error("Không tìm thấy sản phẩm"));
      }
    });

    return promise;
  }

  //IndexedDb
  updateIndexedDb(data: Product) {
    data.titleEn = convertViToEn(data.title);
    data.titleEns = data.titleEn ? data.titleEn.toLowerCase().split(" ") : [];

    indexedDb.products.put(data, data.id);

    const index = _.findIndex(this.products, { id: data.id });
    if (index >= 0) {
      this.products[index] = data;

      if (data.id === "NC7IPUHAX0Nk1uCwMb62") {
        console.log(
          "🚀 ~ ProductService ~ updateIndexedDb ~ this.products[index] / NC7IPUHAX0Nk1uCwMb62:",
          this.products[index],
        );
      }
    } else {
      this.products = [data, ...this.products];
    }
  }

  updateIndexedDbOverview(
    productId: string,
    isReceive: boolean,
    quantity: number,
    amount: number,
  ) {
    const index = _.findIndex(this.products, { id: productId });

    console.log("updateIndexedDbOverview before:", this.products[index]);

    if (index >= 0) {
      this.products[index].issueQuantity += !isReceive ? Number(quantity) : 0;
      this.products[index].issueAmount += !isReceive ? Number(amount) : 0;

      this.products[index].receiveQuantity += isReceive ? Number(quantity) : 0;
      this.products[index].receiveAmount += isReceive ? Number(amount) : 0;

      this.products[index].balanceQuantity += isReceive
        ? Number(quantity)
        : -Number(quantity);

      const numb =
        this.products[index].receiveQuantity != 0
          ? (this.products[index].receiveAmount /
              this.products[index].receiveQuantity) *
            this.products[index].balanceQuantity
          : 0;
      let rounded = Math.round(numb);

      const balanceAmount =
        this.products[index].balanceQuantity > 0 ? rounded : 0;

      this.products[index].balanceAmount = balanceAmount;

      //update price
      if (
        this.products[index].price === 0 &&
        !isReceive &&
        Number(quantity) > 0 &&
        Number(amount) > 0
      ) {
        const numbPrice = Number(amount) / Number(quantity);
        let roundedPrice = Math.round(numbPrice);
        this.products[index].price = roundedPrice;
        this.update(this.products[index]);
      }

      //update cost price
      if (
        this.products[index].costPrice === 0 &&
        isReceive &&
        Number(quantity) > 0 &&
        Number(amount) > 0
      ) {
        const numbPrice = Number(amount) / Number(quantity);
        let roundedPrice = Math.round(numbPrice);
        this.products[index].costPrice = roundedPrice;
        this.update(this.products[index]);
      }

      indexedDb.products.put(this.products[index], productId);

      console.log("updateIndexedDbOverview after:", this.products[index]);

      return this.products[index];
    }

    return null;
  }

  private deleteIndexedDb(id: string) {
    console.log("deleteIndexedDb:", id);

    indexedDb.products.delete(id);

    _.remove(this.products, (item) => {
      return item.id === id;
    });
  }

  calculatorStock(shopId: string) {
    console.log("🚀 ~ ProductService ~ calculatorStock ~ shopId:", shopId);

    localStorage.setItem(
      `${shopId.toLowerCase()}_products_syncedOn`,
      moment().format("YYYY-MM-DD HH:mm"),
    );

    const callable = this.fns.httpsCallable(
      "api-systemConfig-calculateProductsStock",
    );
    return callable({
      shopId: shopId,
    });
  }

  calculateProductStats(
    shopId,
    monthKey,
    productId,
    productCode = "",
    productName = "",
    productUnitName = "",
  ) {
    const callable = this.fns.httpsCallable(
      "api-product-calculateProductStats",
    );
    return callable({
      shopId,
      monthKey,
      productId,
      productCode,
      productName,
      productUnitName,
    });
  }
}
